import styled from '@emotion/styled';
import * as React from 'react';
import { useEffect, useRef, useImperativeHandle } from 'react';
import { isValidBase64Data } from '@hotjar/common';
import { Layer } from '../Layer/Layer';
import { loadImage } from '../../lib/loadImage';
import { screenshotCanvasRenderer } from '../../lib/heatmapCanvasRenderer';
import { registerLayer, unregisterLayer } from '../../lib/heatmapLayersManager';

const Canvas = Layer.withComponent(styled.canvas`
  width: 100%;
`);

interface ScreenshotLayerProps {
  screenshotImageData: string;
  width: number;
  height: number;
}

export interface CanvasRef {
  width: () => number;
  left: () => number;
  height: () => number;
}

export const ScreenshotLayer = React.forwardRef<CanvasRef, ScreenshotLayerProps>(
  (
    { screenshotImageData, width, height }: ScreenshotLayerProps,
    forwardRef,
  ): JSX.Element | null => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useImperativeHandle(forwardRef, () => ({
      width: () => canvasRef.current?.offsetWidth ?? 0,
      left: () => canvasRef.current?.offsetLeft ?? 0,
      height: () => canvasRef.current?.offsetHeight ?? 0,
    }));

    useEffect(() => {
      registerLayer(canvasRef);

      return () => {
        unregisterLayer(canvasRef);
      };
    }, []);

    useEffect(() => {
      loadImage(screenshotImageData).then((screenshotImageElement) => {
        if (canvasRef.current) {
          screenshotCanvasRenderer(canvasRef.current, screenshotImageElement);
        }
      });
    }, [screenshotImageData]);

    if (
      isValidBase64Data(screenshotImageData) === false ||
      isValidBase64Data(screenshotImageData) === ''
    ) {
      return null;
    }

    return <Canvas ref={canvasRef} width={width} height={height} />;
  },
);
